import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Typography } from "@mui/material";
import { format } from "date-fns";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useRetrieveUserIntraSquadTournamentPermissions } from "../api/PermissionApiProvider";
import { useBulkUpdatePlayerIntraSquadTournamentMatchStats, useCreateIntraSquadTournamentMatchResult, useRetrieveIntraSquadTournamentMatch, useUpdateIntraSquadTournamentMatchResult, } from "../api/TournamentApiProvider";
import { useRetrieveTeam, useRetrieveUserTeamPermissions, } from "../ApiProvider";
import { ErrorView } from "../nav/Error";
import { Forbidden } from "../nav/Forbidden";
import { Loading } from "../nav/Loading";
import { IntraSquadTournamentMatchStatsForm } from "../stats/IntraSquadTournamentMatchStatsForm";
import ErrorSnackbar from "../utils/ErrorSnackbar";
import { useNumericParams } from "../utils/useNumericParams";
export const UpdatePlayerIntraSquadTournamentMatchStats = () => {
    const { id } = useNumericParams();
    const { isLoading: useRetrieveIntraSquadTournamentMatchIsLoading, error: useRetrieveIntraSquadTournamentMatchError, data: intraSquadTournamentMatch, } = useRetrieveIntraSquadTournamentMatch({ id });
    const { data: userTournamentPermissions, isLoading: userTournamentPermissionsIsLoading, error: userTournamentPermissionsError, } = useRetrieveUserIntraSquadTournamentPermissions({
        tournamentId: intraSquadTournamentMatch?.tournament,
    });
    const { data: userTeamPermissions, isLoading: userTeamPermissionsIsLoading, error: userTeamPermissionsError, } = useRetrieveUserTeamPermissions({
        teamId: intraSquadTournamentMatch?.team,
    });
    const { canModifyStats: canModifyTournamentStats = false } = userTournamentPermissions || {};
    const { canModifyStats: canModifyTeamStats = false } = userTeamPermissions || {};
    // Stats entry needs stats-write permission (manager or assistant manager),
    // not full team-modify rights.
    const canModify = canModifyTournamentStats || canModifyTeamStats;
    const navigate = useNavigate();
    const { isLoading: useUpdateIntraSquadTournamentMatchStatsIsLoading, isError: useUpdateIntraSquadTournamentMatchStatsIsError, mutateAsync: useBulkUpdatePlayerIntraSquadTournamentMatchStatsMutateAsync, } = useBulkUpdatePlayerIntraSquadTournamentMatchStats(intraSquadTournamentMatch, () => { }, () => undefined, () => { });
    const { isLoading: useUpdateIntraSquadTournamentMatchResultIsLoading, isError: useUpdateIntraSquadTournamentMatchResultMutateAsyncIsError, mutateAsync: useUpdateIntraSquadTournamentMatchResultMutateAsync, } = useUpdateIntraSquadTournamentMatchResult(intraSquadTournamentMatch, () => { }, () => undefined, () => { });
    const { data: team, isLoading: teamIsLoading, error: teamError, } = useRetrieveTeam({
        id: intraSquadTournamentMatch?.team,
        enabled: canModify,
    });
    const { isLoading: useCreateIntraSquadTournamentMatchResultIsLoading, isError: useCreateIntraSquadTournamentMatchResultMutateAsyncIsError, mutateAsync: useCreateIntraSquadTournamentMatchResultMutateAsync, } = useCreateIntraSquadTournamentMatchResult(intraSquadTournamentMatch, () => { }, () => undefined, () => { });
    // Check permissions loading states first, before any data loading
    if (userTournamentPermissionsIsLoading || userTeamPermissionsIsLoading) {
        return _jsx(Loading, {});
    }
    // Check permissions error states first
    if (userTournamentPermissionsError || userTeamPermissionsError) {
        return (_jsx(ErrorView, { error: userTournamentPermissionsError || userTeamPermissionsError }));
    }
    // Check tournament match loading/error after permissions
    if (useRetrieveIntraSquadTournamentMatchIsLoading) {
        return _jsx(Loading, {});
    }
    if (useRetrieveIntraSquadTournamentMatchError) {
        return _jsx(ErrorView, { error: useRetrieveIntraSquadTournamentMatchError });
    }
    // Check permissions before proceeding with any other data fetching
    if (!canModify) {
        return _jsx(Forbidden, {});
    }
    // Now check secondary data loading states (only if permissions allow)
    if (useUpdateIntraSquadTournamentMatchStatsIsLoading ||
        useUpdateIntraSquadTournamentMatchResultIsLoading ||
        useCreateIntraSquadTournamentMatchResultIsLoading ||
        teamIsLoading) {
        return _jsx(Loading, {});
    }
    // Check secondary data error states (only if permissions allow)
    if (teamError) {
        return _jsx(ErrorView, { error: teamError });
    }
    const onSubmit = async (intraSquadTournamentMatchResult, playerIntraSquadTournamentMatchStats) => {
        await useBulkUpdatePlayerIntraSquadTournamentMatchStatsMutateAsync(Object.values(playerIntraSquadTournamentMatchStats));
        if (intraSquadTournamentMatch?.intraSquadTournamentMatchResult === null) {
            await useCreateIntraSquadTournamentMatchResultMutateAsync(intraSquadTournamentMatchResult);
        }
        else {
            await useUpdateIntraSquadTournamentMatchResultMutateAsync(intraSquadTournamentMatchResult);
        }
        navigate(`/teams/${intraSquadTournamentMatch.team}/intra-squad-tournaments/${intraSquadTournamentMatch.tournament}/`);
    };
    const error = useRetrieveIntraSquadTournamentMatchError || teamError;
    if (error) {
        return _jsx(ErrorView, { error: error });
    }
    if (!team || !intraSquadTournamentMatch) {
        return (_jsx(ErrorView, { error: new Error("Failed to load user data correctly.") }));
    }
    return (_jsxs(React.Fragment, { children: [_jsx(ErrorSnackbar, { open: useUpdateIntraSquadTournamentMatchResultMutateAsyncIsError, message: "Failed to update intra-squad-match result! Please try again." }), _jsx(ErrorSnackbar, { open: useCreateIntraSquadTournamentMatchResultMutateAsyncIsError, message: "Failed to create intra-squad-match result! Please try again." }), _jsx(ErrorSnackbar, { open: useUpdateIntraSquadTournamentMatchStatsIsError, message: "Failed to create player intra-squad-match stats! Please try again." }), _jsx(Typography, { variant: "h6", children: _jsxs("strong", { children: ["Intra-Squad Match Stats for Team ", team.name] }) }), _jsx(Typography, { variant: "subtitle2", children: format(new Date(intraSquadTournamentMatch.kickOff), "MMMM do yyyy @ H:mma") }), _jsx(IntraSquadTournamentMatchStatsForm, { onSubmit: onSubmit, intraSquadTournamentMatch: intraSquadTournamentMatch })] }));
};
